import type { Option } from "@tento-lona/sheets";
import { LiveData } from "@tento-core/livedata";
import type { PreferencesStorage } from "./preferences-storage.ts";
import { Internals } from "@tento-lona/sheets/internal-ui";

const STORAGE_KEY = "lona-pref-theme";

/** `ThemePreferences`'s friend record (`Internals`): its constructor registers each instance. */
type ThemePreferencesInternals = {
  readonly store: (prefs: ThemePreferences, name: Option<string>) => void;
};
const themePreferencesInternals = new Internals<ThemePreferences, ThemePreferencesInternals>("ThemePreferences");

/**
 * The selected theme's name (e.g. "grass", "solarized"), stored locally:
 * `ThemesClient`'s own store (SDK-private; `lib.ts` does not export it). Its
 * one writer is `client.themes.setDurable(theme, actor)`, which applies the
 * theme, persists it, stores its name here and announces `ThemeChanged`
 * stamped with the writer; `client.themes.live` is the read.
 */
export class ThemePreferences {
  readonly #current: LiveData<Option<string>>;
  readonly #storage: PreferencesStorage;

  static readonly #internals: ThemePreferencesInternals = {
    store: (prefs, name) => prefs.#store(name),
  };

  constructor(storage: PreferencesStorage) {
    themePreferencesInternals.register(this, ThemePreferences.#internals);
    this.#storage = storage;
    const saved = storage.getItem(STORAGE_KEY);
    this.#current = new LiveData<Option<string>>(saved);
  }

  /** The stored theme name (`null`: the default theme). */
  peek(): Option<string> {
    return this.#current.get();
  }

  #store(name: Option<string>): void {
    if (name) {
      this.#storage.setItem(STORAGE_KEY, name);
    } else {
      this.#storage.removeItem(STORAGE_KEY);
    }
    this.#current.update(name);
  }
}

/**
 * Store the theme name `name` in `prefs` — the storage half of
 * `ThemesClient.setDurable`, the one theme writer.
 *
 * @internal SDK-private (`ThemesClient`); `lib.ts` does not export it.
 */
export function writeThemeName(prefs: ThemePreferences, name: Option<string>): void {
  themePreferencesInternals.of(prefs).store(prefs, name);
}
